import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Button,
  Typography,
  Space,
  Spin,
  message,
  notification,
} from 'antd';
import {
  CloudOutlined,
  DesktopOutlined,
  DatabaseOutlined,
  SafetyOutlined,
  ReloadOutlined,
  FileImageOutlined,
  HddOutlined,
} from '@ant-design/icons';
import { getResources, refreshResources } from '../api/client';
import { accountLabel } from '../utils/accountNames';

const { Title, Text } = Typography;

// ─── Table column definitions ─────────────────────────────────────────────────
//
// Each column maps to a field in the `by_account` array returned by the API.
//
const ACCOUNT_COLUMNS = [
  {
    title: 'Account',
    dataIndex: 'account_id',
    key: 'account_id',
    render: (id) => <Text code>{accountLabel(id)}</Text>,
  },
  {
    title: 'EC2 Instances',
    dataIndex: 'ec2_count',
    key: 'ec2_count',
    align: 'right',
    defaultSortOrder: 'descend',
    sorter: (a, b) => a.ec2_count - b.ec2_count,
  },
  {
    title: 'RDS Instances',
    dataIndex: 'rds_count',
    key: 'rds_count',
    align: 'right',
    sorter: (a, b) => a.rds_count - b.rds_count,
  },
  {
    title: 'Security Groups',
    dataIndex: 'sg_count',
    key: 'sg_count',
    align: 'right',
    sorter: (a, b) => a.sg_count - b.sg_count,
  },
  {
    title: 'VPCs',
    dataIndex: 'vpc_count',
    key: 'vpc_count',
    align: 'right',
    sorter: (a, b) => a.vpc_count - b.vpc_count,
  },
  {
    title: 'AMIs',
    dataIndex: 'ami_count',
    key: 'ami_count',
    align: 'right',
    sorter: (a, b) => a.ami_count - b.ami_count,
  },
  {
    title: 'EFS',
    dataIndex: 'efs_count',
    key: 'efs_count',
    align: 'right',
    sorter: (a, b) => a.efs_count - b.efs_count,
  },
];

// ─── Dashboard Component ──────────────────────────────────────────────────────

const Dashboard = () => {
  // summary holds top-level counts; accountRows is the per-account table data
  const [summary, setSummary] = useState(null);
  const [accountRows, setAccountRows] = useState([]);

  // loading = initial data fetch; refreshing = the "Refresh Data" button action
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // ── Fetch dashboard data ────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getResources();
      const resources = data.resources ?? [];

      // Tally counts from the flat resources array
      let ec2 = 0, rds = 0, sg = 0, vpc = 0, ami = 0, efs = 0;
      let lastRefresh = null;
      const byAccount = {};

      resources.forEach((r) => {
        const type = r.resource_type ?? '';

        // Global counters
        if (type === 'EC2Instance')         ec2++;
        else if (type === 'RDSInstance')    rds++;
        else if (type === 'SecurityGroup')  sg++;
        else if (type === 'VPC')            vpc++;
        else if (type === 'AMI')            ami++;
        else if (type === 'EFS')            efs++;

        // Track most recent update timestamp
        if (r.last_updated && (!lastRefresh || r.last_updated > lastRefresh)) {
          lastRefresh = r.last_updated;
        }

        // Per-account row
        if (!byAccount[r.account_id]) {
          byAccount[r.account_id] = {
            account_id: r.account_id,
            ec2_count: 0,
            rds_count: 0,
            sg_count: 0,
            vpc_count: 0,
            ami_count: 0,
            efs_count: 0,
          };
        }
        const row = byAccount[r.account_id];
        if (type === 'EC2Instance')         row.ec2_count++;
        else if (type === 'RDSInstance')    row.rds_count++;
        else if (type === 'SecurityGroup')  row.sg_count++;
        else if (type === 'VPC')            row.vpc_count++;
        else if (type === 'AMI')            row.ami_count++;
        else if (type === 'EFS')            row.efs_count++;
      });

      setSummary({ total: resources.length, ec2, rds, sg, vpc, ami, efs, last_refresh: lastRefresh });
      setAccountRows(Object.values(byAccount));
    } catch (err) {
      notification.error({ message: 'Failed to load dashboard data', description: 'Please try again.', duration: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  // Run fetchData once when the component first renders
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Refresh handler ─────────────────────────────────────────────────────────
  //
  // Triggers a full AWS re-scan. The Lambda can take 1-2 minutes to complete,
  // which exceeds API Gateway's 29-second timeout. A 502/504 response means
  // the gateway timed out — not that the Lambda failed — so we treat those as
  // success and auto-reload the dashboard after 2 minutes.
  //
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshResources();
      message.success('Refresh started — page will reload in ~2 minutes.');
      setTimeout(fetchData, 120000);
    } catch (err) {
      const status = err.response?.status;
      if (!status || status === 502 || status === 504) {
        // Gateway timeout — Lambda is still running in the background
        message.info('Refresh running in the background. Page will reload in ~2 minutes.');
        setTimeout(fetchData, 120000);
      } else {
        notification.error({ message: 'Refresh failed', description: `Status ${status} — please try again.`, duration: 0 });
      }
    } finally {
      setRefreshing(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    // Spin wraps all content and shows an overlay spinner while loading
    <Spin spinning={loading} tip="Loading dashboard...">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>

        {/* ── Page Header ────────────────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Dashboard
            </Title>
            {summary?.last_refresh && (
              <Text type="secondary" style={{ fontSize: 13 }}>
                Last refreshed: {new Date(summary.last_refresh).toLocaleString()}
              </Text>
            )}
          </div>

          <Button
            type="primary"
            icon={<ReloadOutlined />}
            loading={refreshing}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh Data
          </Button>
        </div>

        {/* ── Stat Cards ─────────────────────────────────────────────────── */}
        {/*
          Row + Col is Ant Design's grid system.
          gutter={[16, 16]} adds 16px gaps horizontally AND vertically.
          xs/sm/lg control how many columns each card spans at each breakpoint.
        */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Resources"
                value={summary?.total ?? 0}
                prefix={<CloudOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="EC2 Instances"
                value={summary?.ec2 ?? 0}
                prefix={<DesktopOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="RDS Instances"
                value={summary?.rds ?? 0}
                prefix={<DatabaseOutlined />}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Security Groups"
                value={summary?.sg ?? 0}
                prefix={<SafetyOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="AMIs"
                value={summary?.ami ?? 0}
                prefix={<FileImageOutlined />}
                valueStyle={{ color: '#13c2c2' }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="EFS File Systems"
                value={summary?.efs ?? 0}
                prefix={<HddOutlined />}
                valueStyle={{ color: '#2f54eb' }}
              />
            </Card>
          </Col>
        </Row>

        {/* ── Per-Account Breakdown Table ─────────────────────────────────── */}
        <Card title="Resources by Account">
          <Table
            dataSource={accountRows}
            columns={ACCOUNT_COLUMNS}
            rowKey="account_id"
            pagination={false}
            size="small"
            locale={{ emptyText: 'No account data available.' }}
          />
        </Card>

      </Space>
    </Spin>
  );
};

export default Dashboard;
